import React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
  makeStyles
} from '@material-ui/core'
import { alpha } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { gql } from 'apollo-boost'
import { useQuery } from '@apollo/react-hooks'
import { useParams } from 'react-router'
import { Link as RouterLink } from 'react-router-dom'
import { AI_SERVICE_BASE_URL } from '../../env'

const COPY_FEEDBACK_TIMEOUT_MS = 1500

type SessionStatus = 'finished' | 'error' | 'in_progress' | string
type BlockKind = 'text' | 'tool_use' | 'tool_result' | 'json' | 'other' | string

interface SessionTurnBlock {
  kind: BlockKind
  title: string
  subtitle: string | null
  summary: string | null
  bodyPreview: string | null
  bodyFull: string | null
  isBodyTruncated: boolean
}

interface SessionTurn {
  role: string
  index: number
  blocks: SessionTurnBlock[]
}

interface ToolCallCount {
  name: string
  count: number
}

interface InputTurnDuplicateInfo {
  seenCount: number
}

type TurnChipEmphasis = 'start' | 'end' | 'middle'

interface TurnChipInfo {
  label: string
  emphasis: TurnChipEmphasis
}

interface SessionInvocation {
  toolCharges?: {
    toolName: string
    billingUnit: string
    quantity: number | null
    unitPriceUsd: number
    estimatedCostUsd: string | null
  }[]
  tokenUsageKnown?: boolean
  invocationId: string
  modelId: string
  executionContext: string | null
  serviceTier: string | null
  startedAtIso: string | null
  finishedAtIso: string | null
  durationMs: number | null
  durationText: string | null
  estimatedCostUsd: string | null
  status: SessionStatus
  inputTokens: number | null
  outputTokens: number | null
  cacheReadTokens: number | null
  cacheWriteTokens: number | null
  toolNames: string[] | null
  reasoningEffort: string | null
  agentLoopStep: number | null
  agentLoopMaxSteps: number | null
  inputRawJson: string | null
  outputRawJson: string | null
  inputTurns: SessionTurn[]
  outputTurns: SessionTurn[]
  errorMessage: string | null
  turnId?: string | null
}

/** Optional deferred rollup aligned with the session record (see AI service session summary). */
interface SessionRollupSummary {
  updatedAt: number
  totalEstimatedCostUsd: string
  userIntentSummary: string
  turnCount?: number
  /** Longer operator-facing description when provided by the API. */
  description?: string | null
}

interface SessionReport {
  sessionId: string
  sessionType: string
  personId: string
  createdAtIso: string
  totalInvocations: number
  finishedInvocations: number
  erroredInvocations: number
  inProgressInvocations: number
  totalDurationMs: number
  totalDurationText: string
  totalEstimatedCostUsd: string
  totalInputTokens: number
  totalOutputTokens: number
  totalCacheReadTokens: number
  totalCacheWriteTokens: number
  invocations: SessionInvocation[]
  summary?: SessionRollupSummary | null
}

interface SessionSummaryResponse {
  data: SessionReport
}

interface RouteParams {
  sessionId: string
}

interface PersonSummaryQuery {
  person: {
    id: string
    firstName: string | null
    lastName: string | null
    account: {
      emailAddress: string | null
    } | null
  } | null
}

interface PersonSummaryQueryVariables {
  personId: string
}

const PERSON_SUMMARY_QUERY = gql`
  query AIToolingSessionSummaryPersonQuery($personId: String!) {
    person(id: $personId) {
      id
      firstName
      lastName
      account {
        emailAddress
      }
    }
  }
`

const useStyles = makeStyles(theme => ({
  pageContainer: {
    paddingBottom: theme.spacing(4)
  },
  sectionHeader: {
    marginBottom: theme.spacing(1)
  },
  sectionDescription: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary
  },
  metricCard: {
    padding: theme.spacing(1.5),
    height: '100%',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary
  },
  flushSectionCard: {
    borderRadius: 0
  },
  aggregateTopRightCard: {
    [theme.breakpoints.up('sm')]: {
      borderLeft: 0
    }
  },
  aggregateBottomLeftCard: {
    [theme.breakpoints.up('sm')]: {
      borderTop: 0
    }
  },
  aggregateBottomRightCard: {
    [theme.breakpoints.up('sm')]: {
      borderTop: 0,
      borderLeft: 0
    }
  },
  aggregateMetricLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4
  },
  aggregateMetricValue: {
    marginTop: theme.spacing(0.75),
    fontSize: 34,
    lineHeight: 1.15,
    fontWeight: 700
  },
  aggregateMetricListCard: {
    padding: theme.spacing(2)
  },
  aggregateMetricListTitle: {
    marginBottom: theme.spacing(1)
  },
  aggregateMetricListRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    '&:not(:last-child)': {
      marginBottom: theme.spacing(0.75)
    }
  },
  aggregateMetricListLabel: {
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  aggregateMetricListValue: {
    fontWeight: 600,
    fontSize: 15
  },
  aggregateTokensCard: {
    padding: theme.spacing(1.5)
  },
  aggregateTokensRows: {
    marginTop: theme.spacing(0.6),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.4)
  },
  aggregateTokensRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1)
  },
  aggregateTokensValue: {
    fontWeight: 600,
    fontSize: 13
  },
  aggregateInvocationHeaderRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1)
  },
  aggregateInvocationLeftColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  aggregateInvocationHeaderStats: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    gap: theme.spacing(0.35)
  },
  aggregateInvocationMetricValue: {
    marginTop: theme.spacing(0.75),
    fontSize: 28,
    lineHeight: 1.1,
    fontWeight: 700
  },
  aggregateInlineStat: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5)
  },
  aggregateInlineStatLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12
  },
  aggregateInlineStatValue: {
    fontWeight: 600,
    fontSize: 13
  },
  monoBlock: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
    color: theme.palette.text.primary,
    background: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1)
  },
  wrapAnywhere: {
    overflowWrap: 'anywhere'
  },
  secondaryText: {
    color: theme.palette.text.secondary
  },
  rollupDescription: {
    marginTop: theme.spacing(0.5),
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    lineHeight: 1.45
  },
  statusChip: {
    textTransform: 'lowercase'
  },
  turnChip: {
    height: 24,
    fontSize: 12
  },
  /** Single hue for all turn chips; middle is softer, start/end share a stronger treatment. */
  turnChipMiddle: {
    backgroundColor: `${alpha(theme.palette.info.main, 0.14)} !important`,
    color: theme.palette.info.dark,
    border: `1px solid ${alpha(theme.palette.info.main, 0.34)}`,
    fontWeight: 500
  },
  turnChipEmphasis: {
    backgroundColor: `${alpha(theme.palette.info.main, 0.3)} !important`,
    color: theme.palette.info.dark,
    border: `2px solid ${alpha(theme.palette.info.main, 0.58)}`,
    fontWeight: 700,
    boxShadow: `0 1px 6px ${alpha(theme.palette.info.main, 0.35)}`
  },
  invocationSummary: {
    width: '100%'
  },
  invocationSummaryTopRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  invocationModelName: {
    fontWeight: 700
  },
  invocationPreviewMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5)
  },
  invocationPreviewMetaItem: {
    minWidth: 180
  },
  inlineMetaRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5)
  },
  inlineMetaLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    whiteSpace: 'nowrap'
  },
  inlineMetaValue: {
    fontSize: 13,
    lineHeight: 1.35
  },
  invocationMetadataGrid: {
    marginTop: theme.spacing(0.5)
  },
  invocationNarrativeLine: {
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.4
  },
  invocationContextLine: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    rowGap: theme.spacing(0.25)
  },
  invocationContextSegment: {
    whiteSpace: 'nowrap'
  },
  invocationContextDivider: {
    margin: theme.spacing(0, 0.75),
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap'
  },
  metadataSection: {
    padding: theme.spacing(1, 1.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.grey[50],
    height: '100%'
  },
  metadataSectionTitle: {
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4
  },
  toolChipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.25)
  },
  toolChip: {
    height: 22
  },
  overviewCardGrid: {
    marginTop: theme.spacing(0.5)
  },
  turnsPanelContainer: {
    marginBottom: theme.spacing(2)
  },
  turnsPanelTitle: {
    marginBottom: theme.spacing(1)
  },
  turnsPanelCard: {
    maxHeight: 520,
    overflowY: 'auto'
  },
  turnCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    background: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary
  },
  duplicateTurnCollapsedCard: {
    cursor: 'pointer'
  },
  duplicateTurnCompactRow: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    cursor: 'pointer'
  },
  duplicateTurnCompactText: {
    fontSize: 12,
    lineHeight: 1.35
  },
  blockCard: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary
  },
  toggleText: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(1),
    display: 'inline-block'
  },
  rawActionsRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  rawCopyButton: {
    minWidth: 0
  },
  accordionSummary: {
    '&.MuiAccordionSummary-root': {
      minHeight: 0
    },
    '&.MuiAccordionSummary-root.Mui-expanded': {
      minHeight: 0
    },
    '& .MuiAccordionSummary-content': {
      margin: theme.spacing(1, 0)
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: theme.spacing(1, 0)
    },
    '& .MuiAccordionSummary-expandIcon': {
      color: theme.palette.text.secondary
    }
  },
  modelSummaryAccordion: {
    marginTop: 0,
    marginBottom: 0,
    borderTop: 0,
    borderRadius: 0,
    '&.MuiAccordion-root': {
      margin: 0
    },
    '&.MuiAccordion-root.Mui-expanded': {
      margin: 0
    },
    '&:before': {
      display: 'none'
    }
  },
  invocationAccordion: {
    marginBottom: 0,
    borderRadius: 0,
    '&.MuiAccordion-root': {
      margin: 0
    },
    '&.MuiAccordion-root.Mui-expanded': {
      margin: 0
    },
    '&:before': {
      display: 'none'
    }
  },
  nonFirstInvocationAccordion: {
    borderTop: 0
  },
  firstInvocationAccordion: {
    borderTop: `1px solid ${theme.palette.divider}`
  },
  modelSummaryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(1)
  },
  modelSummaryRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  modelSummaryToggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.25)
  },
  modelSummaryToggleChip: {
    cursor: 'pointer'
  },
  modelSummaryRow: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25, 1.5)
  },
  modelSummaryRowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  modelSummaryRowMeta: {
    marginTop: theme.spacing(0.75),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75, 2)
  },
  modelSummaryMetaItem: {
    minWidth: 160
  },
  modelSummaryPercent: {
    fontWeight: 600
  },
  modelSummaryEmpty: {
    padding: theme.spacing(1.5)
  }
}))

const formatMaybe = (value: string | number | null | undefined) =>
  value === null || value === undefined ? '-' : String(value)

const PRIMARY_EXECUTION_CONTEXT = 'primary'

const getTurnFingerprint = (turn: SessionTurn) =>
  JSON.stringify({
    role: turn.role || '',
    blocks: (turn.blocks || []).map(block => ({
      kind: block.kind || '',
      title: block.title || '',
      subtitle: block.subtitle || '',
      summary: block.summary || '',
      bodyPreview: block.bodyPreview || '',
      bodyFull: block.bodyFull || '',
      isBodyTruncated: Boolean(block.isBodyTruncated)
    }))
  })

const getTurnInstanceKey = (invocationId: string, turnOffset: number) => `${invocationId}::${turnOffset}`

const sanitizeToolName = (value: string | null | undefined) => {
  const trimmedValue = value?.trim()
  if (!trimmedValue) {
    return null
  }

  const withoutTrailingId = trimmedValue.replace(/\s*\((?:id:\s*)?[a-z0-9_-]{8,}\)\s*$/i, '').trim()

  if (
    !withoutTrailingId ||
    /^id:\s*\S+$/i.test(withoutTrailingId) ||
    /^toolu_[a-z0-9]+$/i.test(withoutTrailingId) ||
    /^call_[a-z0-9]+$/i.test(withoutTrailingId) ||
    /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(withoutTrailingId)
  ) {
    return null
  }

  return withoutTrailingId
}

const toSortedToolCallCounts = (toolCountByName: Map<string, number>) =>
  Array.from(toolCountByName.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count
      }
      return a.name.localeCompare(b.name)
    })

const collectToolCallsFromStructuredOutput = (value: unknown, toolCountByName: Map<string, number>): void => {
  if (!value) {
    return
  }

  if (Array.isArray(value)) {
    value.forEach(item => {
      collectToolCallsFromStructuredOutput(item, toolCountByName)
    })
    return
  }

  if (typeof value !== 'object') {
    return
  }

  const record = value as Record<string, unknown>
  const normalizedType = typeof record.type === 'string' ? record.type.toLowerCase() : null
  const normalizedKind = typeof record.kind === 'string' ? record.kind.toLowerCase() : null
  const isToolUseNode = normalizedType === 'tool_use' || normalizedKind === 'tool_use'
  const isToolResultNode = normalizedType === 'tool_result' || normalizedKind === 'tool_result'

  // Do not walk tool results: they mirror the assistant tool_use and would double-count the same call.
  if (isToolResultNode) {
    return
  }

  let skipToolUseDescent = false

  if (isToolUseNode) {
    const toolName =
      sanitizeToolName(typeof record.name === 'string' ? record.name : null) ||
      (record.toolUse && typeof record.toolUse === 'object'
        ? sanitizeToolName(typeof (record.toolUse as Record<string, unknown>).name === 'string'
            ? ((record.toolUse as Record<string, unknown>).name as string)
            : null)
        : null)
    if (toolName) {
      toolCountByName.set(toolName, (toolCountByName.get(toolName) || 0) + 1)
    }
  } else if (record.toolUse && typeof record.toolUse === 'object') {
    // Some payloads wrap tool-use data under `toolUse` without an explicit type/kind.
    const toolUseRecord = record.toolUse as Record<string, unknown>
    const toolName = sanitizeToolName(typeof toolUseRecord.name === 'string' ? toolUseRecord.name : null)
    if (toolName) {
      toolCountByName.set(toolName, (toolCountByName.get(toolName) || 0) + 1)
      skipToolUseDescent = true
    }
  }

  Object.entries(record).forEach(([key, fieldValue]) => {
    if (key === 'toolUse' && (isToolUseNode || skipToolUseDescent)) {
      return
    }
    collectToolCallsFromStructuredOutput(fieldValue, toolCountByName)
  })
}

const getAssistantToolCallCountsFromRawJson = (rawJson: string | null | undefined): ToolCallCount[] => {
  if (!rawJson) {
    return []
  }

  try {
    const parsed = JSON.parse(rawJson) as unknown
    const toolCountByName = new Map<string, number>()
    collectToolCallsFromStructuredOutput(parsed, toolCountByName)
    return toSortedToolCallCounts(toolCountByName)
  } catch {
    return []
  }
}

const formatDateTimeLocal = (value: string | null | undefined, includeMilliseconds = false) => {
  if (!value) {
    return '-'
  }

  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return value
  }

  if (includeMilliseconds) {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    }).format(parsedDate)
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(parsedDate)
}

const formatRollupUpdatedAt = (ms: number) => {
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) {
    return String(ms)
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(d)
}

const formatNumber = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-'
  }
  return new Intl.NumberFormat().format(value)
}

const formatUsd = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-'
  }
  const valueInCents = value * 100
  const formattedCents = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  }).format(valueInCents)
  return `${formattedCents}¢`
}

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-'
  }
  return `${value.toFixed(1)}%`
}

const parseCostUsd = (value: string | null | undefined) => {
  if (!value) {
    return 0
  }
  const normalized = value.replace(/[^0-9.-]/g, '')
  const parsed = Number.parseFloat(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

const toTimestamp = (isoValue: string | null | undefined) => {
  if (!isoValue) {
    return Number.POSITIVE_INFINITY
  }
  const parsed = new Date(isoValue).getTime()
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed
}

const statusColor = (status: SessionStatus): 'default' | 'primary' | 'secondary' => {
  if (status === 'finished') {
    return 'primary'
  }
  if (status === 'error') {
    return 'secondary'
  }
  return 'default'
}

const isPrimaryInvocation = (invocation: SessionInvocation) =>
  (invocation.executionContext || '').toLowerCase() === PRIMARY_EXECUTION_CONTEXT

const LabeledValue: React.FC<{ label: string; value: string | number | null | undefined; wrap?: boolean }> = ({
  label,
  value,
  wrap
}) => {
  const classes = useStyles()

  return (
    <Box mb={1}>
      <Typography variant='caption' className={classes.secondaryText}>
        {label}
      </Typography>
      <Typography variant='body2' className={wrap ? classes.wrapAnywhere : undefined}>
        {formatMaybe(value)}
      </Typography>
    </Box>
  )
}

const MetricCard: React.FC<{ label: string; value: string | number | null | undefined; className?: string }> = ({
  label,
  value,
  className
}) => {
  const classes = useStyles()

  return (
    <Card className={`${classes.metricCard} ${className || ''}`}>
      <Typography className={classes.aggregateMetricLabel}>{label}</Typography>
      <Typography className={classes.aggregateMetricValue}>{formatMaybe(value)}</Typography>
    </Card>
  )
}

const InlineLabeledValue: React.FC<{ label: string; value: string | number | null | undefined; wrap?: boolean }> = ({
  label,
  value,
  wrap
}) => {
  const classes = useStyles()

  return (
    <Box className={classes.inlineMetaRow}>
      <Typography className={classes.inlineMetaLabel}>{label}:</Typography>
      <Typography className={`${classes.inlineMetaValue} ${wrap ? classes.wrapAnywhere : ''}`}>
        {formatMaybe(value)}
      </Typography>
    </Box>
  )
}

const ExpandableText: React.FC<{ preview: string | null; full: string | null; isTruncated: boolean }> = ({
  preview,
  full,
  isTruncated
}) => {
  const classes = useStyles()
  const [showFull, setShowFull] = React.useState(false)

  const textToShow = showFull && full ? full : preview || full || ''

  return (
    <>
      <pre className={classes.monoBlock}>{textToShow}</pre>
      {isTruncated && full ? (
        <Typography
          variant='body2'
          className={classes.toggleText}
          onClick={() => {
            setShowFull(!showFull)
          }}>
          {showFull ? 'Show preview content' : 'Show full content'}
        </Typography>
      ) : null}
    </>
  )
}

const TurnRenderer: React.FC<{ turn: SessionTurn; duplicateInfo?: InputTurnDuplicateInfo }> = ({
  turn,
  duplicateInfo
}) => {
  const classes = useStyles()
  const [isCollapsed, setIsCollapsed] = React.useState(Boolean(duplicateInfo))

  React.useEffect(() => {
    setIsCollapsed(Boolean(duplicateInfo))
  }, [duplicateInfo, turn.index, turn.role])

  const duplicateSeenSuffix = duplicateInfo
    ? `${duplicateInfo.seenCount} prior ${duplicateInfo.seenCount === 1 ? 'time' : 'times'}`
    : null
  const handleToggleDuplicateCollapse = () => {
    if (!duplicateInfo) {
      return
    }
    setIsCollapsed(!isCollapsed)
  }
  const handleDuplicateRowClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    handleToggleDuplicateCollapse()
  }
  const handleDuplicateKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    handleToggleDuplicateCollapse()
  }

  return (
    <Card
      className={`${classes.turnCard} ${duplicateInfo && isCollapsed ? classes.duplicateTurnCollapsedCard : ''}`}
      onClick={duplicateInfo && isCollapsed ? handleToggleDuplicateCollapse : undefined}
      onKeyDown={duplicateInfo && isCollapsed ? handleDuplicateKeyDown : undefined}
      role={duplicateInfo && isCollapsed ? 'button' : undefined}
      tabIndex={duplicateInfo && isCollapsed ? 0 : undefined}>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Typography variant='subtitle2'>Turn {turn.index}</Typography>
        <Chip label={turn.role} size='small' />
      </Box>
      {duplicateInfo ? (
        <Box
          className={classes.duplicateTurnCompactRow}
          onClick={handleDuplicateRowClick}
          onKeyDown={handleDuplicateKeyDown}
          role='button'
          tabIndex={0}>
          <Typography variant='body2' className={`${classes.secondaryText} ${classes.duplicateTurnCompactText}`}>
            Already shown ({duplicateSeenSuffix})
          </Typography>
          <Chip label={isCollapsed ? 'Expand' : 'Collapse'} size='small' />
        </Box>
      ) : null}
      {duplicateInfo && isCollapsed ? null : (
        <>
          {turn.blocks.map((block, index) => (
            <Card className={classes.blockCard} key={`${turn.index}-${block.kind}-${index}`}>
              <Typography variant='subtitle2'>
                {block.title}
                {block.subtitle ? ` - ${block.subtitle}` : ''}
              </Typography>
              {block.summary ? (
                <Typography variant='body2' className={classes.secondaryText}>
                  {block.summary}
                </Typography>
              ) : null}
              <Box mt={1}>
                <ExpandableText preview={block.bodyPreview} full={block.bodyFull} isTruncated={block.isBodyTruncated} />
              </Box>
            </Card>
          ))}
        </>
      )}
    </Card>
  )
}

const TurnsPanel: React.FC<{
  title: string
  viewType: 'input' | 'output'
  turns: SessionTurn[]
  rawJson: string | null
  rawToggleText: string
  invocationId: string
  duplicateInputTurnInfoByKey?: Map<string, InputTurnDuplicateInfo>
}> = ({ title, viewType, turns, rawJson, rawToggleText, invocationId, duplicateInputTurnInfoByKey }) => {
  const classes = useStyles()
  const [showRaw, setShowRaw] = React.useState(false)
  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'copied' | 'error'>('idle')
  const copyFeedbackTimerRef = React.useRef<number | null>(null)
  const rawJsonText = rawJson || ''
  const hasRawJson = !!rawJsonText

  React.useEffect(() => {
    return () => {
      if (copyFeedbackTimerRef.current) {
        window.clearTimeout(copyFeedbackTimerRef.current)
      }
    }
  }, [])

  const scheduleCopyFeedbackReset = () => {
    if (copyFeedbackTimerRef.current) {
      window.clearTimeout(copyFeedbackTimerRef.current)
    }
    copyFeedbackTimerRef.current = window.setTimeout(() => {
      setCopyStatus('idle')
      copyFeedbackTimerRef.current = null
    }, COPY_FEEDBACK_TIMEOUT_MS)
  }

  const handleCopyRawJson = async () => {
    if (!hasRawJson) {
      return
    }
    try {
      if (!navigator.clipboard) {
        throw new Error('Clipboard API unavailable')
      }
      await navigator.clipboard.writeText(rawJsonText)
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
    scheduleCopyFeedbackReset()
  }

  return (
    <Box className={classes.turnsPanelContainer}>
      <Typography variant='h6' className={classes.turnsPanelTitle}>
        {title}
      </Typography>
      <Card className={`${classes.card} ${classes.turnsPanelCard}`}>
        {turns.length ? (
          turns.map((turn, turnOffset) => {
            const duplicateInfo =
              viewType === 'input' && duplicateInputTurnInfoByKey
                ? duplicateInputTurnInfoByKey.get(getTurnInstanceKey(invocationId, turnOffset))
                : undefined
            return (
              <TurnRenderer
                key={`${title}-${invocationId}-${turn.index}-${turn.role}-${turnOffset}`}
                turn={turn}
                duplicateInfo={duplicateInfo}
              />
            )
          })
        ) : (
          <Typography variant='body2' className={classes.secondaryText}>
            No turns available.
          </Typography>
        )}
        <Divider />
        <Box className={classes.rawActionsRow}>
          <Typography
            variant='body2'
            className={classes.toggleText}
            onClick={() => {
              setShowRaw(!showRaw)
            }}>
            {showRaw ? 'Hide raw JSON' : rawToggleText}
          </Typography>
          <Button
            size='small'
            variant='outlined'
            className={classes.rawCopyButton}
            disabled={!hasRawJson}
            onClick={() => {
              void handleCopyRawJson()
            }}>
            {copyStatus === 'copied' ? 'Copied' : 'Copy JSON'}
          </Button>
          {copyStatus === 'error' ? (
            <Typography variant='caption' className={classes.secondaryText}>
              Copy failed
            </Typography>
          ) : null}
        </Box>
        {showRaw ? (
          <Box mt={1}>
            <pre className={classes.monoBlock}>{rawJsonText}</pre>
          </Box>
        ) : null}
      </Card>
    </Box>
  )
}

export const AIToolingSessionSummary = () => {
  const classes = useStyles()
  const turnChipClassName = (emphasis: TurnChipEmphasis) =>
    `${classes.turnChip} ${
      emphasis === 'middle' ? classes.turnChipMiddle : classes.turnChipEmphasis
    }`
  const { getAccessTokenSilently } = useAuth0()
  const { sessionId } = useParams<RouteParams>()
  const [report, setReport] = React.useState<SessionReport | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [costBreakdownView, setCostBreakdownView] = React.useState<'model' | 'invocation'>('model')
  const [invocationFilter, setInvocationFilter] = React.useState<'all' | 'primary'>('all')
  const personIdForLookup = report?.personId?.trim() || ''
  const shouldLookupPerson = !!personIdForLookup && personIdForLookup !== '-'
  const { data: personLookupData, loading: personLookupLoading } = useQuery<
    PersonSummaryQuery,
    PersonSummaryQueryVariables
  >(PERSON_SUMMARY_QUERY, {
    variables: {
      personId: personIdForLookup
    },
    skip: !shouldLookupPerson
  })

  const totalCostUsd = React.useMemo(() => {
    if (!report) {
      return 0
    }
    const summedCosts = report.invocations.reduce(
      (sum, invocation) => sum + parseCostUsd(invocation.estimatedCostUsd),
      0
    )
    return summedCosts || parseCostUsd(report.totalEstimatedCostUsd)
  }, [report])

  const sortedInvocations = React.useMemo(() => {
    if (!report) {
      return []
    }

    return [...report.invocations].sort((a, b) => {
      const startedDiff = toTimestamp(a.startedAtIso) - toTimestamp(b.startedAtIso)
      if (startedDiff !== 0) {
        return startedDiff
      }

      const finishedDiff = toTimestamp(a.finishedAtIso) - toTimestamp(b.finishedAtIso)
      if (finishedDiff !== 0) {
        return finishedDiff
      }

      return a.invocationId.localeCompare(b.invocationId)
    })
  }, [report])

  /**
   * Labels + styling emphasis for invocations that share a `turnId` (sorted session order),
   * turn numbers 1-based by first appearance of each distinct `turnId`.
   */
  const turnChipInfoByInvocationId = React.useMemo(() => {
    const turnNumberByTurnId = new Map<string, number>()
    const invocationsByTurnId = new Map<string, SessionInvocation[]>()
    let nextTurn = 1

    for (const inv of sortedInvocations) {
      const tid = inv.turnId?.trim()
      if (!tid) {
        continue
      }
      if (!turnNumberByTurnId.has(tid)) {
        turnNumberByTurnId.set(tid, nextTurn)
        nextTurn += 1
      }
      const list = invocationsByTurnId.get(tid)
      if (list) {
        list.push(inv)
      } else {
        invocationsByTurnId.set(tid, [inv])
      }
    }

    const infoById = new Map<string, TurnChipInfo>()
    invocationsByTurnId.forEach((group, tid) => {
      const n = turnNumberByTurnId.get(tid)
      if (n === undefined) {
        return
      }
      const only = group.length === 1
      group.forEach((inv: SessionInvocation, idx: number) => {
        const inProgress = (inv.status || '').toLowerCase() === 'in_progress'
        const isFirst = idx === 0
        const isLast = idx === group.length - 1
        let label: string
        let emphasis: TurnChipEmphasis
        if (only) {
          if (inProgress) {
            label = `Turn ${n} start`
            emphasis = 'start'
          } else {
            label = `Turn ${n} end`
            emphasis = 'end'
          }
        } else if (isFirst) {
          label = `Turn ${n} start`
          emphasis = 'start'
        } else if (isLast) {
          if (inProgress) {
            label = `Turn ${n}`
            emphasis = 'middle'
          } else {
            label = `Turn ${n} end`
            emphasis = 'end'
          }
        } else {
          label = `Turn ${n}`
          emphasis = 'middle'
        }
        infoById.set(inv.invocationId, { label, emphasis })
      })
    })
    return infoById
  }, [sortedInvocations])

  const modelCostBreakdown = React.useMemo(() => {
    if (!report) {
      return []
    }

    const models = new Map<
      string,
      {
        modelId: string
        invocations: number
        inputTokens: number
        outputTokens: number
        cacheReadTokens: number
        cacheWriteTokens: number
        totalCostUsd: number
      }
    >()

    sortedInvocations.forEach(invocation => {
      const modelId = invocation.modelId || 'unknown'
      const current = models.get(modelId) || {
        modelId,
        invocations: 0,
        inputTokens: 0,
        outputTokens: 0,
        cacheReadTokens: 0,
        cacheWriteTokens: 0,
        totalCostUsd: 0
      }

      current.invocations += 1
      current.inputTokens += invocation.inputTokens || 0
      current.outputTokens += invocation.outputTokens || 0
      current.cacheReadTokens += invocation.cacheReadTokens || 0
      current.cacheWriteTokens += invocation.cacheWriteTokens || 0
      current.totalCostUsd += parseCostUsd(invocation.estimatedCostUsd)

      models.set(modelId, current)
    })

    return Array.from(models.values())
      .map(item => ({
        ...item,
        totalTokens: item.inputTokens + item.outputTokens + item.cacheReadTokens + item.cacheWriteTokens,
        costSharePercent: totalCostUsd > 0 ? (item.totalCostUsd / totalCostUsd) * 100 : 0
      }))
      .sort((a, b) => b.totalCostUsd - a.totalCostUsd)
  }, [report, sortedInvocations, totalCostUsd])

  const invocationCostBreakdown = React.useMemo(() => {
    if (!sortedInvocations.length) {
      return []
    }

    return sortedInvocations.map(invocation => {
      const totalCost = parseCostUsd(invocation.estimatedCostUsd)
      const inputTokens = invocation.inputTokens || 0
      const outputTokens = invocation.outputTokens || 0
      const cacheReadTokens = invocation.cacheReadTokens || 0
      const cacheWriteTokens = invocation.cacheWriteTokens || 0
      return {
        invocationId: invocation.invocationId,
        modelId: invocation.modelId || 'unknown',
        totalCostUsd: totalCost,
        costSharePercent: totalCostUsd > 0 ? (totalCost / totalCostUsd) * 100 : 0,
        inputTokens,
        outputTokens,
        cacheReadTokens,
        cacheWriteTokens,
        totalTokens: inputTokens + outputTokens + cacheReadTokens + cacheWriteTokens,
        turnChipInfo: turnChipInfoByInvocationId.get(invocation.invocationId)
      }
    })
  }, [sortedInvocations, totalCostUsd, turnChipInfoByInvocationId])

  const filteredInvocations = React.useMemo(() => {
    if (invocationFilter === 'primary') {
      return sortedInvocations.filter(isPrimaryInvocation)
    }
    return sortedInvocations
  }, [invocationFilter, sortedInvocations])

  const duplicateInputTurnInfoByKey = React.useMemo(() => {
    const seenByFingerprint = new Map<string, { count: number; invocationIds: Set<string> }>()
    const duplicateInfoByTurnKey = new Map<string, InputTurnDuplicateInfo>()

    sortedInvocations.forEach(invocation => {
      if (!isPrimaryInvocation(invocation)) {
        return
      }

      const processTurn = (turn: SessionTurn, turnOffset: number, panel: 'input' | 'output') => {
        const fingerprint = getTurnFingerprint(turn)
        const existing = seenByFingerprint.get(fingerprint)

        if (panel === 'input' && existing?.count) {
          duplicateInfoByTurnKey.set(getTurnInstanceKey(invocation.invocationId, turnOffset), {
            seenCount: existing.count
          })
        }

        const nextInvocationIds = existing?.invocationIds || new Set<string>()
        nextInvocationIds.add(invocation.invocationId)
        seenByFingerprint.set(fingerprint, {
          count: (existing?.count || 0) + 1,
          invocationIds: nextInvocationIds
        })
      }

      ;(invocation.inputTurns || []).forEach((turn, turnOffset) => {
        processTurn(turn, turnOffset, 'input')
      })
      ;(invocation.outputTurns || []).forEach((turn, turnOffset) => {
        processTurn(turn, turnOffset, 'output')
      })
    })

    return duplicateInfoByTurnKey
  }, [sortedInvocations])

  const toolCountsByInvocationId = React.useMemo(() => {
    const countsByInvocationId = new Map<string, ToolCallCount[]>()

    sortedInvocations.forEach(invocation => {
      const toolCountsFromRawJson = getAssistantToolCallCountsFromRawJson(invocation.outputRawJson)
      countsByInvocationId.set(invocation.invocationId, toolCountsFromRawJson)
    })

    return countsByInvocationId
  }, [sortedInvocations])

  const personName = React.useMemo(() => {
    const firstName = personLookupData?.person?.firstName?.trim() || ''
    const lastName = personLookupData?.person?.lastName?.trim() || ''
    const fullName = `${firstName} ${lastName}`.trim()
    return fullName || null
  }, [personLookupData])

  React.useEffect(() => {
    const getSummary = async () => {
      if (!AI_SERVICE_BASE_URL) {
        setError('Missing API base URL. Configure AI_SERVICE_ENDPOINT in root env.')
        return
      }

      setLoading(true)
      setError(null)

      try {
        const token = await getAccessTokenSilently()
        const response = await fetch(`${AI_SERVICE_BASE_URL}/admin/sessions/${encodeURIComponent(sessionId)}/summary`, {
          headers: {
            'X-Vestaboard-Token': token
          }
        })

        if (!response.ok) {
          throw new Error(`Failed to load session summary (${response.status})`)
        }

        const json = (await response.json()) as SessionSummaryResponse
        setReport(json.data)
      } catch (fetchError) {
        const message = fetchError instanceof Error ? fetchError.message : 'Failed to load session summary.'
        setError(message)
      } finally {
        setLoading(false)
      }
    }

    getSummary()
  }, [getAccessTokenSilently, sessionId])

  return (
    <Box className={classes.pageContainer}>
      <Box mb={1}>
        <Link component={RouterLink} to='/ai-tooling/sessions' color='primary' variant='body2'>
          ← AI sessions list
        </Link>
      </Box>
      <Typography variant='h4'>AI Session Report</Typography>
      <Typography variant='body2' className={classes.sectionDescription}>
        Session overview, aggregate metrics, and per-invocation inputs/outputs.
      </Typography>

      {loading ? <CircularProgress /> : null}
      {error ? <Alert severity='error'>{error}</Alert> : null}

      {!loading && !error && report ? (
        <>
          <Typography variant='h6' className={classes.sectionHeader}>
            Session Overview
          </Typography>
          <Card className={classes.card}>
            <Grid container spacing={2} className={classes.overviewCardGrid}>
              <Grid item xs={12} sm={6}>
                <LabeledValue label='Session ID' value={report.sessionId} wrap />
                <LabeledValue label='Session Type' value={report.sessionType} />
                <LabeledValue label='Created At' value={formatDateTimeLocal(report.createdAtIso)} wrap />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabeledValue label='Person ID' value={report.personId} wrap />
                {shouldLookupPerson ? (
                  personLookupLoading ? (
                    <LabeledValue label='Person Name' value='Loading...' />
                  ) : personLookupData?.person ? (
                    <>
                      <LabeledValue label='Person Name' value={personName || '-'} />
                      <LabeledValue
                        label='Person Email'
                        value={personLookupData.person.account?.emailAddress || '-'}
                        wrap
                      />
                    </>
                  ) : null
                ) : null}
              </Grid>
            </Grid>
          </Card>

          <Typography variant='h6' className={classes.sectionHeader}>
            Aggregate Metrics
          </Typography>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6} md={6}>
              <MetricCard
                label='Estimated Cost (¢)'
                value={formatUsd(parseCostUsd(report.totalEstimatedCostUsd))}
                className={classes.flushSectionCard}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <MetricCard
                label='Total Duration'
                value={report.totalDurationText}
                className={`${classes.flushSectionCard} ${classes.aggregateTopRightCard}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Card
                className={`${classes.metricCard} ${classes.aggregateMetricListCard} ${classes.flushSectionCard} ${classes.aggregateBottomLeftCard}`}>
                <Box className={classes.aggregateInvocationHeaderRow}>
                  <Box className={classes.aggregateInvocationLeftColumn}>
                    <Typography className={classes.aggregateMetricLabel}>Total Invocations</Typography>
                    <Typography className={classes.aggregateInvocationMetricValue}>
                      {formatNumber(report.totalInvocations)}
                    </Typography>
                  </Box>
                  <Box className={classes.aggregateInvocationHeaderStats}>
                    <Box className={classes.aggregateInlineStat}>
                      <Typography className={classes.aggregateInlineStatLabel}>Finished</Typography>
                      <Typography className={classes.aggregateInlineStatValue}>
                        {formatNumber(report.finishedInvocations)}
                      </Typography>
                    </Box>
                    <Box className={classes.aggregateInlineStat}>
                      <Typography className={classes.aggregateInlineStatLabel}>Errored</Typography>
                      <Typography className={classes.aggregateInlineStatValue}>
                        {formatNumber(report.erroredInvocations)}
                      </Typography>
                    </Box>
                    <Box className={classes.aggregateInlineStat}>
                      <Typography className={classes.aggregateInlineStatLabel}>In Progress</Typography>
                      <Typography className={classes.aggregateInlineStatValue}>
                        {formatNumber(report.inProgressInvocations)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Card
                className={`${classes.metricCard} ${classes.aggregateTokensCard} ${classes.flushSectionCard} ${classes.aggregateBottomRightCard}`}>
                <Typography className={classes.aggregateMetricLabel}>Tokens + Cache</Typography>
                <Box className={classes.aggregateTokensRows}>
                  <Box className={classes.aggregateTokensRow}>
                    <Typography className={classes.aggregateInlineStatLabel}>Input / Output</Typography>
                    <Typography className={classes.aggregateTokensValue}>
                      {formatNumber(report.totalInputTokens)} / {formatNumber(report.totalOutputTokens)}
                    </Typography>
                  </Box>
                  <Box className={classes.aggregateTokensRow}>
                    <Typography className={classes.aggregateInlineStatLabel}>Cache Read / Write</Typography>
                    <Typography className={classes.aggregateTokensValue}>
                      {formatNumber(report.totalCacheReadTokens)} / {formatNumber(report.totalCacheWriteTokens)}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>

          <Accordion className={`${classes.card} ${classes.modelSummaryAccordion}`}>
            <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMoreIcon />}>
              <Box className={classes.modelSummaryHeader}>
                <Typography variant='subtitle1'>Model Usage & Cost Breakdown</Typography>
                <Typography variant='body2' className={classes.secondaryText}>
                  {costBreakdownView === 'model'
                    ? `${modelCostBreakdown.length} models`
                    : `${invocationCostBreakdown.length} invocations`}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box width='100%'>
                <Box className={classes.modelSummaryToggleRow}>
                  <Typography variant='body2' className={classes.secondaryText}>
                    View:
                  </Typography>
                  <Chip
                    label='Per model'
                    size='small'
                    color={costBreakdownView === 'model' ? 'primary' : 'default'}
                    onClick={() => setCostBreakdownView('model')}
                    className={classes.modelSummaryToggleChip}
                  />
                  <Chip
                    label='Per invocation'
                    size='small'
                    color={costBreakdownView === 'invocation' ? 'primary' : 'default'}
                    onClick={() => setCostBreakdownView('invocation')}
                    className={classes.modelSummaryToggleChip}
                  />
                </Box>
                {(costBreakdownView === 'model' ? modelCostBreakdown.length : invocationCostBreakdown.length) ? (
                  <Box className={classes.modelSummaryRows}>
                    {costBreakdownView === 'model'
                      ? modelCostBreakdown.map(model => {
                          const heat = Math.min(1, model.costSharePercent / 100)
                          const backgroundColor = `rgba(244, 67, 54, ${0.06 + heat * 0.3})`
                          const borderColor = `rgba(244, 67, 54, ${0.18 + heat * 0.5})`

                          return (
                            <Box
                              key={model.modelId}
                              className={classes.modelSummaryRow}
                              style={{
                                backgroundColor,
                                borderColor
                              }}>
                              <Box className={classes.modelSummaryRowHeader}>
                                <Typography variant='subtitle2' className={classes.wrapAnywhere}>
                                  {model.modelId}
                                </Typography>
                                <Typography variant='body2' className={classes.modelSummaryPercent}>
                                  {formatPercent(model.costSharePercent)} of cost
                                </Typography>
                              </Box>
                              <Box className={classes.modelSummaryRowMeta}>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Cost (¢)
                                  </Typography>
                                  <Typography variant='body2'>{formatUsd(model.totalCostUsd)}</Typography>
                                </Box>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Invocations
                                  </Typography>
                                  <Typography variant='body2'>{formatNumber(model.invocations)}</Typography>
                                </Box>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Total Tokens
                                  </Typography>
                                  <Typography variant='body2'>{formatNumber(model.totalTokens)}</Typography>
                                </Box>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Input / Output
                                  </Typography>
                                  <Typography variant='body2'>
                                    {formatNumber(model.inputTokens)} / {formatNumber(model.outputTokens)}
                                  </Typography>
                                </Box>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Cache Read / Write
                                  </Typography>
                                  <Typography variant='body2'>
                                    {formatNumber(model.cacheReadTokens)} / {formatNumber(model.cacheWriteTokens)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )
                        })
                      : invocationCostBreakdown.map(invocation => {
                          const heat = Math.min(1, invocation.costSharePercent / 100)
                          const backgroundColor = `rgba(244, 67, 54, ${0.06 + heat * 0.3})`
                          const borderColor = `rgba(244, 67, 54, ${0.18 + heat * 0.5})`

                          return (
                            <Box
                              key={invocation.invocationId}
                              className={classes.modelSummaryRow}
                              style={{
                                backgroundColor,
                                borderColor
                              }}>
                              <Box className={classes.modelSummaryRowHeader}>
                                <Box display='flex' alignItems='center' flexWrap='wrap' style={{ gap: 8 }}>
                                  {invocation.turnChipInfo ? (
                                    <Chip
                                      size='small'
                                      label={invocation.turnChipInfo.label}
                                      className={turnChipClassName(invocation.turnChipInfo.emphasis)}
                                    />
                                  ) : null}
                                  <Typography variant='subtitle2' className={classes.wrapAnywhere}>
                                    {invocation.modelId}
                                  </Typography>
                                </Box>
                                <Typography variant='body2' className={classes.modelSummaryPercent}>
                                  {formatPercent(invocation.costSharePercent)} of cost
                                </Typography>
                              </Box>
                              <Box className={classes.modelSummaryRowMeta}>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Invocation ID
                                  </Typography>
                                  <Typography variant='body2' className={classes.wrapAnywhere}>
                                    {invocation.invocationId}
                                  </Typography>
                                </Box>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Cost (¢)
                                  </Typography>
                                  <Typography variant='body2'>{formatUsd(invocation.totalCostUsd)}</Typography>
                                </Box>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Total Tokens
                                  </Typography>
                                  <Typography variant='body2'>{formatNumber(invocation.totalTokens)}</Typography>
                                </Box>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Input / Output
                                  </Typography>
                                  <Typography variant='body2'>
                                    {formatNumber(invocation.inputTokens)} / {formatNumber(invocation.outputTokens)}
                                  </Typography>
                                </Box>
                                <Box className={classes.modelSummaryMetaItem}>
                                  <Typography variant='caption' className={classes.secondaryText}>
                                    Cache Read / Write
                                  </Typography>
                                  <Typography variant='body2'>
                                    {formatNumber(invocation.cacheReadTokens)} /{' '}
                                    {formatNumber(invocation.cacheWriteTokens)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )
                        })}
                  </Box>
                ) : (
                  <Box className={classes.modelSummaryEmpty}>
                    <Typography variant='body2' className={classes.secondaryText}>
                      No model usage found.
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Box mt={3} mb={1}>
            <Typography variant='h6' className={classes.sectionHeader}>
              Invocations (
              {invocationFilter === 'primary'
                ? `${formatNumber(filteredInvocations.length)} of ${formatNumber(report.invocations.length)}`
                : formatNumber(report.invocations.length)}
              )
            </Typography>
            <Typography variant='body2' className={classes.sectionDescription}>
              Expand any invocation to inspect metadata and raw input/output turns.
            </Typography>
            <Box className={classes.modelSummaryToggleRow}>
              <Typography variant='body2' className={classes.secondaryText}>
                Filter:
              </Typography>
              <Chip
                label='All'
                size='small'
                color={invocationFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setInvocationFilter('all')}
                className={classes.modelSummaryToggleChip}
              />
              <Chip
                label='Primary only'
                size='small'
                color={invocationFilter === 'primary' ? 'primary' : 'default'}
                onClick={() => setInvocationFilter('primary')}
                className={classes.modelSummaryToggleChip}
              />
            </Box>
          </Box>

          {filteredInvocations.length ? (
            filteredInvocations.map((invocation, index) => {
            const toolsToRender = toolCountsByInvocationId.get(invocation.invocationId) || []
            const invocationTurnChipInfo = turnChipInfoByInvocationId.get(invocation.invocationId)

            return (
              <Accordion
                key={invocation.invocationId}
                className={`${classes.card} ${classes.invocationAccordion} ${
                  index === 0 ? classes.firstInvocationAccordion : classes.nonFirstInvocationAccordion
                }`}>
                <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMoreIcon />}>
                  <Box className={classes.invocationSummary}>
                    <Box className={classes.invocationSummaryTopRow}>
                      <Typography variant='subtitle1' className={classes.invocationModelName}>
                        {formatMaybe(invocation.modelId)}
                      </Typography>
                      {invocationTurnChipInfo ? (
                        <Chip
                          size='small'
                          label={invocationTurnChipInfo.label}
                          className={turnChipClassName(invocationTurnChipInfo.emphasis)}
                        />
                      ) : null}
                      <Chip
                        className={classes.statusChip}
                        color={statusColor(invocation.status)}
                        size='small'
                        label={invocation.status}
                      />
                      {isPrimaryInvocation(invocation) &&
                      invocation.status !== 'in_progress' &&
                      (invocation.cacheReadTokens || 0) === 0 ? (
                        <Chip className={classes.statusChip} color='secondary' size='small' label='Cache miss' />
                      ) : null}
                      {toolsToRender.map(tool => (
                        <Chip
                          key={`${invocation.invocationId}-used-${tool.name}`}
                          label={tool.count > 1 ? `${tool.name} (${tool.count})` : tool.name}
                          size='small'
                          className={classes.toolChip}
                        />
                      ))}
                    </Box>
                    <Box className={classes.invocationPreviewMeta}>
                      <Box className={classes.invocationPreviewMetaItem}>
                        <InlineLabeledValue
                          label='Started'
                          value={formatDateTimeLocal(invocation.startedAtIso, true)}
                        />
                      </Box>
                      <Box className={classes.invocationPreviewMetaItem}>
                        <InlineLabeledValue label='Duration' value={invocation.durationText} />
                      </Box>
                      <Box className={classes.invocationPreviewMetaItem}>
                        <InlineLabeledValue
                          label='Estimated Cost (¢)'
                          value={invocation.estimatedCostUsd === null ? 'Unknown' : formatUsd(parseCostUsd(invocation.estimatedCostUsd))}
                        />
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Box width='100%'>
                    <Grid container spacing={2} className={classes.invocationMetadataGrid}>
                      <Grid item xs={12} md={6} lg={4}>
                        <Box className={classes.metadataSection}>
                          <Typography className={classes.metadataSectionTitle}>Context</Typography>
                          <Typography
                            variant='body2'
                            className={`${classes.invocationNarrativeLine} ${classes.wrapAnywhere}`}>
                            Invocation ID: {formatMaybe(invocation.invocationId)}
                          </Typography>
                          <Typography
                            variant='body2'
                            className={`${classes.invocationNarrativeLine} ${classes.invocationContextLine}`}>
                            <span className={classes.invocationContextSegment}>
                              Execution context: {formatMaybe(invocation.executionContext)}
                            </span>
                            <span className={classes.invocationContextDivider}>/</span>
                            <span className={classes.invocationContextSegment}>
                              Service tier: {formatMaybe(invocation.serviceTier)}
                            </span>
                            {invocation.reasoningEffort ? (
                              <>
                                <span className={classes.invocationContextDivider}>/</span>
                                <span className={classes.invocationContextSegment}>
                                  Reasoning effort: {invocation.reasoningEffort}
                                </span>
                              </>
                            ) : null}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <Box className={classes.metadataSection}>
                          <Typography className={classes.metadataSectionTitle}>Timing</Typography>
                          <Typography variant='body2' className={classes.invocationNarrativeLine}>
                            Started: {formatDateTimeLocal(invocation.startedAtIso, true)}
                          </Typography>
                          <Typography variant='body2' className={classes.invocationNarrativeLine}>
                            Finished: {formatDateTimeLocal(invocation.finishedAtIso, true)}
                          </Typography>
                          <Typography variant='body2' className={classes.invocationNarrativeLine}>
                            Duration: {formatMaybe(invocation.durationText)}
                          </Typography>
                          <Typography variant='body2' className={classes.invocationNarrativeLine}>
                            Agent loop: step {formatMaybe(invocation.agentLoopStep)} of{' '}
                            {formatMaybe(invocation.agentLoopMaxSteps)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <Box className={classes.metadataSection}>
                          <Typography className={classes.metadataSectionTitle}>Usage</Typography>
                          {invocation.toolCharges?.map((charge, index) => (
                            <Typography key={index} variant='body2' className={classes.invocationNarrativeLine}>
                              {charge.toolName}: {charge.quantity ?? 'unknown'} {charge.billingUnit};
                              estimated cost (USD): {charge.estimatedCostUsd ?? 'unknown'}.
                            </Typography>
                          ))}
                          {invocation.tokenUsageKnown === false && (
                            <Typography variant='body2' className={classes.invocationNarrativeLine}>
                              Token usage was not returned.
                            </Typography>
                          )}
                          <Typography variant='body2' className={classes.invocationNarrativeLine}>
                            Tokens: {formatMaybe(invocation.inputTokens)} in / {formatMaybe(invocation.outputTokens)}{' '}
                            out
                          </Typography>
                          <Typography variant='body2' className={classes.invocationNarrativeLine}>
                            Cache: {formatMaybe(invocation.cacheReadTokens)} read /{' '}
                            {formatMaybe(invocation.cacheWriteTokens)} write
                          </Typography>
                          <Typography variant='body2' className={classes.secondaryText}>
                            Available Tools
                          </Typography>
                          <Box className={classes.toolChipsRow}>
                            {invocation.toolNames?.length ? (
                              invocation.toolNames.map(toolName => (
                                <Chip
                                  key={`${invocation.invocationId}-${toolName}`}
                                  label={toolName}
                                  size='small'
                                  className={classes.toolChip}
                                />
                              ))
                            ) : (
                              <Typography variant='body2' className={classes.secondaryText}>
                                -
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {invocation.errorMessage ? (
                      <Box mt={2}>
                        <Alert severity='error'>{invocation.errorMessage}</Alert>
                      </Box>
                    ) : null}

                    <Box mt={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TurnsPanel
                            title='Input'
                            viewType='input'
                            turns={invocation.inputTurns || []}
                            rawJson={invocation.inputRawJson}
                            rawToggleText='Show raw input JSON'
                            invocationId={invocation.invocationId}
                            duplicateInputTurnInfoByKey={duplicateInputTurnInfoByKey}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TurnsPanel
                            title='Output'
                            viewType='output'
                            turns={invocation.outputTurns || []}
                            rawJson={invocation.outputRawJson}
                            rawToggleText='Show raw output JSON'
                            invocationId={invocation.invocationId}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )
            })
          ) : (
            <Card className={classes.card}>
              <Typography variant='body2' className={classes.secondaryText}>
                No invocations match the selected filter.
              </Typography>
            </Card>
          )}

          {report.summary ? (
            <Box mt={3}>
              <Typography variant='h6' className={classes.sectionHeader}>
                Deferred summary
              </Typography>
              <Card className={classes.card}>
                {report.summary.description != null && String(report.summary.description).trim() !== '' ? (
                  <Box mb={2}>
                    <Typography variant='caption' color='textSecondary' display='block' gutterBottom>
                      Description
                    </Typography>
                    <Typography variant='body1' className={classes.rollupDescription}>
                      {report.summary.description}
                    </Typography>
                  </Box>
                ) : null}
                <LabeledValue label='User intent' value={report.summary.userIntentSummary} wrap />
                {report.summary.turnCount != null ? (
                  <Box mt={1}>
                    <LabeledValue label='Primary turns' value={report.summary.turnCount} />
                  </Box>
                ) : null}
                <Box mt={1}>
                  <LabeledValue label='Rollup updated' value={formatRollupUpdatedAt(report.summary.updatedAt)} wrap />
                </Box>
              </Card>
            </Box>
          ) : null}
        </>
      ) : null}
    </Box>
  )
}
